import { useAgencyFilter } from 'hooks/useAgencyFilter';
import {
  States,
  CampaignStatuses,
  ContractDashboardRow,
  ContractStatuses,
  SettlementDateStatuses,
  useStaffGetContractDashboard,
  VendorBankDetailsStatuses,
  SortingOptionContractDashboard,
} from '@urbanx/agx.campaigns.client';
import type { SelectChipOption, SortOrder } from '@urbanx/agx.ui-components2';
import { useEffect, useState } from 'react';
import { splitPascalCase } from 'utils/stringUtils';
import {
  contractsDashboardSettlementDateStatuses,
  createInitialContractsDashboardFilterState,
  storeContractsDashboardFilterState,
} from './ContractsDashboardFilterState';

export interface AgencyLookupValue {
  name: string;
  logoUrl?: string;
}

const createAgencyLookup = (
  agencies?: { id?: string; name?: string; logoUrl?: string }[]
): Record<string, AgencyLookupValue> =>
  (agencies ?? []).reduce(
    (lookup, agency) => {
      if (agency.id && agency.name) {
        lookup[agency.id] = {
          name: agency.name,
          logoUrl: agency.logoUrl,
        };
      }

      return lookup;
    },
    {} as Record<string, AgencyLookupValue>
  );

const campaignStatusOptions = Object.values(CampaignStatuses).map((status) => ({
  label: splitPascalCase(status).replace(/^Listing\s+/, ''),
  value: status,
}));

const vendorBankDetailsStatusOptions = Object.values(VendorBankDetailsStatuses).map((status) => ({
  label: status.toLowerCase() === 'na' ? 'N/A' : status,
  value: status,
}));

const contractStatusOptions = Object.values(ContractStatuses).map((status) => ({
  label: splitPascalCase(status),
  value: status,
}));

const settlementDateStatusOptions = contractsDashboardSettlementDateStatuses.map((status) => ({
  label: splitPascalCase(status),
  value: status,
}));

const settlementSortOptions = [
  {
    label: 'Oldest first',
    value: SortingOptionContractDashboard.SettlementDateAsc,
  },
  {
    label: 'Newest first',
    value: SortingOptionContractDashboard.SettlementDateDesc,
  },
];

const stateOptions = [
  { label: 'QLD', value: States.Qld },
  { label: 'NSW', value: States.Nsw },
];

const buildSelectedOptions = (
  options: { label: string; value: string }[],
  selectedValues?: string[]
) => {
  return options.map((option) => ({
    ...option,
    selected: selectedValues?.includes(option.value) ?? false,
  }));
};

const sortContractDashboardRows = (
  rows: ContractDashboardRow[],
  sortingOption?: SortingOptionContractDashboard
) => {
  if (!sortingOption) {
    return rows;
  }

  const sortDirection = sortingOption === SortingOptionContractDashboard.SettlementDateAsc ? 1 : -1;

  return [...rows].sort((leftRow, rightRow) => {
    const leftSettlementTime = leftRow.settlementDate
      ? new Date(leftRow.settlementDate).getTime()
      : null;
    const rightSettlementTime = rightRow.settlementDate
      ? new Date(rightRow.settlementDate).getTime()
      : null;

    if (leftSettlementTime === rightSettlementTime) {
      return 0;
    }

    if (leftSettlementTime === null) {
      return 1;
    }

    if (rightSettlementTime === null) {
      return -1;
    }

    return (leftSettlementTime - rightSettlementTime) * sortDirection;
  });
};

const useCampaignsDashboard = () => {
  const [initialFilterState] = useState(createInitialContractsDashboardFilterState);
  const [selectedAgencyValues, setSelectedAgencyValues] = useState<string[] | undefined>(
    initialFilterState.agencyIds
  );
  const [selectedStates, setSelectedStates] = useState<States[] | undefined>(
    initialFilterState.states
  );
  const [selectedContractStatuses, setSelectedContractStatuses] = useState<
    ContractStatuses[] | undefined
  >(initialFilterState.contractStatuses);
  const [selectedSettlementDateStatuses, setSelectedSettlementDateStatuses] = useState<
    SettlementDateStatuses[] | undefined
  >(initialFilterState.settlementDateStatuses);
  const [selectedVendorBankDetailsStatuses, setSelectedVendorBankDetailsStatuses] = useState<
    VendorBankDetailsStatuses[] | undefined
  >(initialFilterState.vendorBankDetailsStatuses);
  const [selectedSortingOption, setSelectedSortingOption] = useState<
    SortingOptionContractDashboard | undefined
  >(undefined);
  const [addressSearchText, setAddressSearchText] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<ContractDashboardRow[]>([]);
  const {
    agencies,
    agencyOptions: agencyFilterOptions,
    effectiveAgencyIds,
    isPending: isAgencyFilterPending,
    isAgencyScopeUnavailable,
    hasNoMatchingAgencies,
  } = useAgencyFilter({ selectedValues: selectedAgencyValues });
  const dashboardEnabled =
    !isAgencyFilterPending && !isAgencyScopeUnavailable && !hasNoMatchingAgencies;

  const { data, isPending } = useStaffGetContractDashboard(
    effectiveAgencyIds,
    selectedStates,
    selectedContractStatuses,
    selectedSettlementDateStatuses,
    selectedVendorBankDetailsStatuses,
    addressSearchText,
    undefined,
    dashboardEnabled
  );

  useEffect(() => {
    setRows(dashboardEnabled ? sortContractDashboardRows(data ?? [], selectedSortingOption) : []);
  }, [dashboardEnabled, data, selectedSortingOption]);

  useEffect(() => {
    storeContractsDashboardFilterState({
      states: selectedStates,
      agencyIds: selectedAgencyValues,
      contractStatuses: selectedContractStatuses,
      settlementDateStatuses: selectedSettlementDateStatuses,
      vendorBankDetailsStatuses: selectedVendorBankDetailsStatuses,
    });
  }, [
    selectedAgencyValues,
    selectedStates,
    selectedContractStatuses,
    selectedSettlementDateStatuses,
    selectedSortingOption,
    selectedVendorBankDetailsStatuses,
  ]);

  const agencyLookup = createAgencyLookup(agencies);

  const onSortSettlement = (sortOrder: SortOrder) => {
    const nextSortingOption =
      sortOrder === 1
        ? SortingOptionContractDashboard.SettlementDateAsc
        : sortOrder === -1
          ? SortingOptionContractDashboard.SettlementDateDesc
          : undefined;

    setSelectedSortingOption(nextSortingOption);
  };

  const selectedCampaignStatusOptions = buildSelectedOptions(
    campaignStatusOptions,
    selectedContractStatuses
  );

  const selectedStateOptions = buildSelectedOptions(stateOptions, selectedStates);

  const selectedContractStatusOptions = buildSelectedOptions(
    contractStatusOptions,
    selectedContractStatuses
  );

  const selectedSettlementDateOptions = buildSelectedOptions(
    settlementDateStatusOptions,
    selectedSettlementDateStatuses
  );

  const selectedVendorBankDetailsOptions = buildSelectedOptions(
    vendorBankDetailsStatusOptions,
    selectedVendorBankDetailsStatuses
  );

  const selectedSettlementSortOptions = buildSelectedOptions(
    settlementSortOptions,
    selectedSortingOption ? [selectedSortingOption] : undefined
  );

  const handleAgencyFilterSelect = (options: SelectChipOption[]) => {
    setSelectedAgencyValues(options.length > 0 ? options.map((option) => option.value) : undefined);
  };

  const handleStateFilterSelect = (options: SelectChipOption[]) => {
    setSelectedStates(
      options.length > 0 ? options.map((option) => option.value as States) : undefined
    );
  };

  const handleContractStatusFilterSelect = (options: SelectChipOption[]) => {
    setSelectedContractStatuses(
      options.length > 0 ? options.map((option) => option.value as ContractStatuses) : undefined
    );
  };

  const handleSettlementDateFilterSelect = (options: SelectChipOption[]) => {
    setSelectedSettlementDateStatuses(
      options.length > 0
        ? options.map((option) => option.value as SettlementDateStatuses)
        : undefined
    );
  };

  const handleVendorBankDetailsFilterSelect = (options: SelectChipOption[]) => {
    setSelectedVendorBankDetailsStatuses(
      options.length > 0
        ? options.map((option) => option.value as VendorBankDetailsStatuses)
        : undefined
    );
  };

  const handleSettlementSortSelect = (options: SelectChipOption[]) => {
    setSelectedSortingOption(
      options.length > 0 ? (options[0].value as SortingOptionContractDashboard) : undefined
    );
  };

  return {
    rows,
    agencyLookup,
    isPending: isAgencyFilterPending || (dashboardEnabled && isPending),
    isAgenciesPending: isAgencyFilterPending,
    addressSearchText,
    agencyFilterOptions,
    selectedStateOptions,
    selectedCampaignStatusOptions,
    selectedContractStatusOptions,
    selectedSettlementDateOptions,
    selectedSettlementSortOptions,
    selectedVendorBankDetailsOptions,
    setAddressSearchText,
    handleStateFilterSelect,
    handleAgencyFilterSelect,
    handleContractStatusFilterSelect,
    handleSettlementDateFilterSelect,
    handleSettlementSortSelect,
    handleVendorBankDetailsFilterSelect,
    onSortSettlement,
    resetStateFilters: () => setSelectedStates(undefined),
    resetAgencyFilters: () => setSelectedAgencyValues(undefined),
    resetContractStatusFilters: () => setSelectedContractStatuses(undefined),
    resetSettlementDateFilters: () => setSelectedSettlementDateStatuses(undefined),
    resetSettlementSort: () => setSelectedSortingOption(undefined),
    resetVendorBankDetailsFilters: () => setSelectedVendorBankDetailsStatuses(undefined),
  };
};

export default useCampaignsDashboard;
